import { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useBranding } from '@/hooks/useBranding';
import { useLocalization } from '@/hooks/useLocalization';
import { useGuestExitPreventionOptional } from '@/contexts/GuestExitPreventionContext';

interface BrandLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
}

const SIZE_CLASSES: Record<NonNullable<BrandLogoProps['size']>, string> = {
  sm: 'h-6',
  md: 'h-8',
  lg: 'h-10',
  xl: 'h-12',
  '2xl': 'h-14',
  '3xl': 'h-16',
  '4xl': 'h-20',
};

/**
 * Centralized brand logo component
 * 
 * Automatically switches between Tax.Network and Tributário logos based on:
 * - Domain (tributaro.com.br)
 * - Path (/brasil)
 * - User location selection (BR)
 * 
 * When clicked on the home page, scrolls to top for clear feedback.
 * When guest has active chat session, shows exit prevention modal.
 */
export const BrandLogo = ({ className = '', size = 'md' }: BrandLogoProps) => {
  const { brand } = useBranding();
  const { getHomePath } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const guestExitPrevention = useGuestExitPreventionOptional();

  const homePath = getHomePath();
  const isOnHomePage = location.pathname === homePath || location.pathname === '/';

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      // If on home page, just scroll to top
      if (isOnHomePage) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Check if guest exit prevention should intercept
      if (guestExitPrevention?.requestNavigation(homePath)) {
        e.preventDefault();
        return;
      }

      // Otherwise, let the Link handle navigation normally
    },
    [isOnHomePage, guestExitPrevention, homePath]
  );

  return (
    <Link
      to={homePath}
      onClick={handleClick}
      className={cn(
        'inline-flex items-center shrink-0 cursor-pointer',
        'focus-visible:outline-none focus-visible:border-ring rounded-sm',
        className
      )}
      aria-label={`Go to ${brand.displayName} home`}
    >
      <img
        src={brand.logoSrc}
        alt={brand.logoAlt}
        className={cn('w-auto object-contain', SIZE_CLASSES[size])}
        draggable={false}
      />
    </Link>
  );
};
